import { ChevronDown, ChevronUp } from 'lucide-react'
import { ReactNode, useState } from 'react'
import { Button } from '@/components/ui/button.tsx'
import { cn } from '@/lib/utils.ts'

interface ExpandableButtonProps {
	// Button content props
	buttonContent?: ReactNode

	// Button props
	buttonVariant?: 'default' | 'destructive' | 'outline' | 'secondary' | 'ghost' | 'link'
	buttonClassName?: string

	// Content props
	expandedContent: ReactNode

	// State props
	defaultExpanded?: boolean
	onExpandedChange?: (expanded: boolean) => void

	// Icon props
	expandIcon?: ReactNode
	collapseIcon?: ReactNode
	showIcon?: boolean

	// Container props
	containerClassName?: string
}

export function ExpandableButton({
	buttonContent,
	buttonVariant = 'outline',
	buttonClassName = '',
	expandedContent,
	defaultExpanded = false,
	onExpandedChange,
	expandIcon = <ChevronDown className="h-4 w-4" />,
	collapseIcon = <ChevronUp className="h-4 w-4" />,
	showIcon = true,
	containerClassName = '',
}: ExpandableButtonProps) {
	const [isExpanded, setIsExpanded] = useState(defaultExpanded)

	const handleClick = () => {
		const newExpandedState = !isExpanded
		setIsExpanded(newExpandedState)
		onExpandedChange?.(newExpandedState)
	}

	// Default button content if none provided
	const defaultButtonContent = (
		<div className="flex items-center justify-between w-full">
			<span>Click to {isExpanded ? 'collapse' : 'expand'}</span>
			{showIcon && (isExpanded ? collapseIcon : expandIcon)}
		</div>
	)

	return (
		<Button onClick={handleClick} className={cn('w-full p-4 h-auto', buttonClassName)} variant={buttonVariant}>
			<div className={cn('w-full flex flex-col', containerClassName)}>
				{buttonContent || defaultButtonContent}
				<div
					style={{
						height: isExpanded ? 'auto' : '0px',
						marginTop: isExpanded ? '16px' : '0px',
						opacity: isExpanded ? 1 : 0,
						transition: 'all 300ms ease-in-out',
						overflow: 'hidden',
					}}>
					{expandedContent}
				</div>
			</div>
		</Button>
	)
}
